import {
  SignupBody,
  SignupResponse,
  useTypeSafeMutation,
} from '@oresundsbron/api';
import { flow, pipe } from 'fp-ts/lib/function';
import { FC, useCallback, useMemo, useState } from 'react';
import { Lens } from 'monocle-ts';
import { SignupPayement } from '../reepay';
import { setError, setResponse, setToken, useSignUp } from './stores/useSignUp';
import {
  fromNullable,
  getOrElseW,
  map,
  match,
  Applicative,
  chain,
  fromPredicate,
  altW,
  of,
  getOrElse,
  toUndefined,
} from 'fp-ts/lib/Option';
import { sequenceS } from 'fp-ts/lib/Apply';
import { z } from 'zod';
import {
  BooleanValue,
  businessCommuter,
  isBusiness,
} from '@oresundsbron/validators';
import { isBackendError } from '../../lib/errors';
import { Box } from '@oresundsbron/bridge-ui';
import { tapO } from '@oresundsbron/utilities';
import * as gtag from '~/lib/gtag';
import { getContracts } from '~/stores/contracts';
import {
  filter,
  findFirst,
  flatten,
  map as mapA,
  reduce,
} from 'fp-ts/lib/Array';
import { strMatcher } from '~/lib/matchers';
import { decodePhoneCode } from '~/lib/phone-code';
import { match as matchB } from 'fp-ts/lib/boolean';
import { BusinessCustomerData } from '~/components/Forms/ContactInformation/Business';
import {
  businessPhoneCode,
  businessPrivatePhoneCode,
  privatePhoneCode,
} from './utils';

type BusinessCommuter = z.input<typeof businessCommuter>;

const paymentMethod = Lens.fromProp<SignupBody>()('paymentMethod');

const currency = Lens.fromPath<SignupBody>()(['termsOfAgreement', 'currency']);
const contactInformation = Lens.fromProp<SignupBody>()('contactInformation');
const contractType = Lens.fromProp<SignupBody>()('contractType');
const campaignCode = Lens.fromPath<SignupBody>()([
  'termsOfAgreement',
  'campaignCode',
]);

const paymentEntries = Lens.fromPath<SignupBody>()([
  'payment',
  'paymentEntries',
]);

const HGV = (data: SignupBody): BooleanValue =>
  pipe(
    data,
    paymentEntries.get,
    findFirst((x) => x.weightClass === 'true'),
    match(
      () => 'false',
      () => 'true'
    )
  );

const hasHGV = (data?: SignupBody) => pipe(data, fromNullable, map(HGV));
const getContractType = (data?: SignupBody) =>
  pipe(data, fromNullable, map(contractType.get));
const getCurrency = (data?: SignupBody) =>
  pipe(data, fromNullable, map(currency.get));
const getStartDate = (data?: SignupBody) =>
  pipe(
    data,
    fromNullable,
    map(contactInformation.get),
    chain((d) =>
      pipe(
        d,
        fromPredicate(
          (
            x
          ): x is SignupBody['contactInformation'] & {
            commuter: BusinessCommuter;
          } => !!(x as { commuter?: BusinessCommuter }).commuter
        ),
        map((c) => c.commuter.startDate),
        altW(() => of(undefined))
      )
    )
  );
const getCampaignCode = (data?: SignupBody) =>
  pipe(data, fromNullable, map(campaignCode.get));

const setPaymentInformation =
  (resp: Reepay.Response, session: string) => (data?: SignupBody) =>
    pipe(
      data,
      fromNullable,
      map(
        paymentMethod.set({
          method: 'card',
          card: { data: { ...resp, error: '' }, session },
        })
      )
    );

const onError = (e: unknown) =>
  pipe(
    e,
    fromPredicate(isBackendError),
    match(
      () => {},
      (e) => setError(e)
    )
  );

const onSuccess = (body: SignupBody) => (data: unknown) => {
  const response = data as SignupResponse;
  setResponse(response);
  setToken(response.code);

  const { contracts } = getContracts();
  let info = {
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    phoneCode: '',
  };
  if ((body as any).contactInformation.contact) {
    info = (body as any).contactInformation.contact;
  } else {
    info = (body as any).contactInformation;
  }
  gtag.resetPurchase();
  gtag.purchase(
    {
      transaction_id: response.orderNo,
      value: pipe(
        contracts,
        mapA((contract) => contract.prices),
        flatten,
        filter((price) => price.currency === body.termsOfAgreement.currency),
        reduce(0, (res, price) => res + price.priceInclVat),
        (totalPrice) => `${totalPrice}`
      ),
      currency: body.termsOfAgreement.currency,
      coupon: body.termsOfAgreement.campaignCode,
      items: contracts.map((contract) => ({
        // See ticket 7308
        item_name: pipe(
          contract.value as
            | 'OresundGO'
            | 'Oresund_Business'
            | 'OresundPendler'
            | 'Oresund_Business_Pendler'
            | 'OresundPAY'
            | 'OresundPAYBusiness'
            | 'Storebelt',
          strMatcher({
            Oresund_Business_Pendler: () => 'OresundCommuter',
            Oresund_Business: () => 'OresundBusiness',
            OresundPAYBusiness: () => 'OresundPay',
            OresundPendler: () => 'OresundCommuter',
            OresundGO: () => 'OresundGo',
            OresundPAY: () => 'OresundPay',
            Storebelt: () => 'Storebelt',
          })
        ),
        item_category: pipe(
          contract.value as
            | 'OresundGO'
            | 'Oresund_Business'
            | 'OresundPendler'
            | 'Oresund_Business_Pendler'
            | 'OresundPAY'
            | 'OresundPAYBusiness'
            | 'Storebelt',
          strMatcher({
            Oresund_Business_Pendler: () => 'Business',
            Oresund_Business: () => 'Business',
            OresundPAYBusiness: () => 'Business',
            OresundPendler: () => 'Private',
            OresundGO: () => 'Private',
            OresundPAY: () => 'Private',
            Storebelt: () => 'Private',
          })
        ),
        item_variant: 'OperatorSignup',
        quantity: '1',
        price: pipe(
          contract.prices,
          findFirst(
            (price) => price.currency === body.termsOfAgreement.currency
          ),
          map((price) => `${price.priceInclVat}`),
          getOrElse(() => '')
        ),
      })),
    },
    {
      email: info.email,
      firstName: info.firstName,
      lastName: info.lastName,
      phone: `+${body.contactInformation.phoneCode}${body.contactInformation.phone}`,
      city: body.contactInformation.city,
      address1: body.contactInformation.address,
      zipCode: body.contactInformation.zipCode,
      company: (body as any).contactInformation.companyName ?? '',
      country: body.contactInformation.country,
    }
  );
  body.subscribeToClub &&
    gtag.subscribeToNewsLetterOnPurchase(body.contractType);
};

export const CreditCardPayment: FC = () => {
  const { data } = useSignUp();
  const [loading, toggleLoading] = useState(false);
  const { mutateAsync } = useTypeSafeMutation(
    ['signup'],
    (req) => req.Customer.signup,
    {
      onError,
      onSuccess: onSuccess(data as SignupBody),
      onSettled: () => toggleLoading(false),
    }
  );

  const tokenInformation = useMemo(
    () =>
      pipe(
        sequenceS(Applicative)({
          isHGV: hasHGV(data),
          currency: getCurrency(data),
          identity: getContractType(data),
          campaignCode: getCampaignCode(data),
          startDate: getStartDate(data),
        }),
        getOrElseW(() => {
          setError({ code: 500, error: 'no signup data initialized' });
          return undefined;
        })
      ),
    [data]
  );

  const mapContactInfo = useCallback(
    (signupBody: SignupBody) =>
      pipe(
        signupBody,
        fromNullable,
        map(
          flow(
            isBusiness,
            matchB(
              () => pipe(signupBody, privatePhoneCode.modify(decodePhoneCode)),
              () =>
                ({
                  ...signupBody,
                  contactInformation: pipe(
                    signupBody.contactInformation as BusinessCustomerData,
                    businessPrivatePhoneCode.modify(decodePhoneCode),
                    businessPhoneCode.modify(decodePhoneCode)
                  ),
                } as SignupBody)
            )
          )
        ),
        getOrElse(() => signupBody)
      ),
    []
  );

  const _onSuccess = useCallback(
    (resp: Reepay.Response, session: string) =>
      pipe(
        data,
        fromNullable,
        map(
          flow(
            mapContactInfo,
            setPaymentInformation(resp, session),
            tapO(() => toggleLoading(true)),
            map(mutateAsync),
            match(
              () => {},
              () => {}
            )
          )
        ),
        toUndefined
      ),
    [data, mutateAsync, mapContactInfo]
  );

  if (loading) {
    return (
      <Box className="item-center col-span-full flex h-[600px] items-center justify-center text-5xl text-primary-600 md:col-span-6 md:col-start-4">
        <svg
          className="animate-spin"
          fill="currentColor"
          width="1em"
          height="1em"
          viewBox="0 0 24 24"
        >
          <path
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </Box>
    );
  }

  return (
    <SignupPayement
      tokenInformation={tokenInformation}
      onError={onError}
      onSuccess={_onSuccess}
    />
  );
};
